import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPost } from '@/lib/api';
import type {
  Asset,
  BillableMilestone,
  Burndown,
  BusinessHours,
  CannedResponse,
  Contract,
  ContractProfitability,
  PendingApproval,
  Profitability,
  Project,
  ProjectSummary,
  RunningTimer,
  SlaCompliance,
  SlaPolicy,
  Sprint,
  Ticket,
  TicketBoard,
  TimeEntry,
  TimesheetWeek,
  UnbilledTime,
  Utilization,
  Velocity,
  WorkBoard,
  WorkItem,
} from './types';

function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  ) as Partial<T>;
}

/**
 * Delivery and the service desk are joined at the hip through time entries: a
 * minute logged on a ticket moves a project's cost, a contract's hour bank and
 * a timesheet at once. Invalidating them together is cheaper than tracking
 * which of the four a given write touched.
 */
function invalidateDelivery(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['projects', 'work-items', 'sprints', 'time-entries', 'timesheets']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

function invalidateService(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of ['tickets', 'contracts', 'service', 'time-entries']) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

// ── Projects ─────────────────────────────────────────────────────────────────

export function useProjects(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => apiList<Project[]>('/projects', { params: clean(params) }),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => apiGet<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useProjectSummary(id: string | undefined) {
  return useQuery({
    queryKey: ['projects', id, 'summary'],
    queryFn: () => apiGet<ProjectSummary>(`/projects/${id}/summary`),
    enabled: Boolean(id),
  });
}

export function useProfitability(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['projects', id, 'profitability'],
    queryFn: () => apiGet<Profitability>(`/projects/${id}/profitability`),
    enabled: Boolean(id) && enabled,
    retry: false,
  });
}

export function useSaveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<Project>(id ? `/projects/${id}` : '/projects', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useSetProjectMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, members }: { id: string; members: unknown[] }) =>
      apiPost<Project>(`/projects/${id}/members`, { members }),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useCloseProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, checklist }: { id: string; checklist: Record<string, boolean> }) =>
      apiPost<Project>(`/projects/${id}/close`, { checklist }),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/projects/${id}`),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

// ── Milestones, change requests, risks ───────────────────────────────────────

export function useSaveMilestone(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/projects/${projectId}/milestones`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useSetMilestoneStatus(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, ...body }: Record<string, unknown> & { milestoneId: string }) =>
      apiPost(`/projects/${projectId}/milestones/${milestoneId}/status`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useBillableMilestones(projectId?: string) {
  return useQuery({
    queryKey: ['projects', 'billable-milestones', projectId],
    queryFn: () =>
      apiGet<BillableMilestone[]>('/projects/billable-milestones', {
        params: clean({ projectId }),
      }),
    retry: false,
  });
}

export function useSaveChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/projects/${projectId}/change-requests`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useDecideChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ changeRequestId, ...body }: Record<string, unknown> & { changeRequestId: string }) =>
      apiPost(`/projects/${projectId}/change-requests/${changeRequestId}/decide`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useSaveRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/projects/${projectId}/risks`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

// ── Sprints ──────────────────────────────────────────────────────────────────

export function useSprints(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprints', projectId],
    queryFn: () => apiGet<Sprint[]>(`/projects/${projectId}/sprints`),
    enabled: Boolean(projectId),
  });
}

export function useBurndown(projectId: string | undefined, sprintId: string | undefined) {
  return useQuery({
    queryKey: ['sprints', projectId, sprintId, 'burndown'],
    queryFn: () => apiGet<Burndown>(`/projects/${projectId}/sprints/${sprintId}/burndown`),
    enabled: Boolean(projectId && sprintId),
  });
}

export function useVelocity(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprints', projectId, 'velocity'],
    queryFn: () => apiGet<Velocity>(`/projects/${projectId}/velocity`),
    enabled: Boolean(projectId),
  });
}

export function useSaveSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/projects/${projectId}/sprints`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useStartSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sprintId: string) => apiPost(`/projects/${projectId}/sprints/${sprintId}/start`),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useCompleteSprint(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sprintId, ...body }: Record<string, unknown> & { sprintId: string }) =>
      apiPost(`/projects/${projectId}/sprints/${sprintId}/complete`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

// ── Work items ───────────────────────────────────────────────────────────────

export function useWorkItems(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['work-items', params],
    queryFn: () => apiList<WorkItem[]>('/work-items', { params: clean(params) }),
  });
}

export function useWorkBoard(projectId: string | undefined, sprintId?: string) {
  return useQuery({
    queryKey: ['work-items', 'board', projectId, sprintId],
    queryFn: () => apiGet<WorkBoard>('/work-items/board', { params: clean({ projectId, sprintId }) }),
    enabled: Boolean(projectId),
  });
}

export function useWorkItem(id: string | undefined) {
  return useQuery({
    queryKey: ['work-items', id],
    queryFn: () => apiGet<WorkItem>(`/work-items/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<WorkItem>(id ? `/work-items/${id}` : '/work-items', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useMoveWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      apiPost<WorkItem>(`/work-items/${id}/move`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useCommentOnItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/work-items/${id}/comments`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-items', id] }),
  });
}

export function useLinkWorkItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/work-items/${id}/links`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-items'] }),
  });
}

export function useDeleteWorkItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/work-items/${id}`),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

// ── Timesheets ───────────────────────────────────────────────────────────────

export function useTimesheetWeek(userId?: string, weekStart?: string) {
  return useQuery({
    queryKey: ['timesheets', 'week', userId, weekStart],
    queryFn: () => apiGet<TimesheetWeek>('/time-entries/week', { params: clean({ userId, weekStart }) }),
  });
}

export function useTimeEntries(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['time-entries', params],
    queryFn: () => apiList<TimeEntry[]>('/time-entries', { params: clean(params) }),
  });
}

export function useRunningTimer() {
  return useQuery({
    queryKey: ['time-entries', 'timer'],
    queryFn: () => apiGet<RunningTimer>('/time-entries/timer'),
    // The elapsed figure is read from the server, so it stays honest across
    // a reload or a second tab rather than counting from when this one opened.
    refetchInterval: 60_000,
  });
}

export function useSaveTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<TimeEntry>(id ? `/time-entries/${id}` : '/time-entries', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/time-entries/${id}`),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useStartTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/time-entries/timer/start', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useStopTimer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown> = {}) => apiPost('/time-entries/timer/stop', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useSubmitTimesheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown> = {}) => apiPost('/time-entries/submit', body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: ['timesheets', 'approvals'],
    queryFn: () => apiGet<PendingApproval[]>('/time-entries/approvals'),
    retry: false,
  });
}

export function useDecideTimesheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      apiPost(`/timesheets/${id}/decide`, body),
    onSuccess: () => invalidateDelivery(queryClient),
  });
}

export function useUtilization(params: { from: string; to: string; userId?: string; approvedOnly?: boolean }) {
  return useQuery({
    queryKey: ['timesheets', 'utilization', params],
    queryFn: () => apiGet<Utilization>('/time-entries/utilization', { params: clean(params) }),
  });
}

export function useUnbilledTime(params: { projectId?: string; from?: string; to?: string; groupBy?: string }) {
  return useQuery({
    queryKey: ['time-entries', 'unbilled', params],
    queryFn: () => apiGet<UnbilledTime>('/time-entries/unbilled', { params: clean(params) }),
    enabled: Boolean(params.projectId),
    retry: false,
  });
}

// ── Tickets ──────────────────────────────────────────────────────────────────

export function useTickets(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['tickets', params],
    queryFn: () => apiList<Ticket[]>('/tickets', { params: clean(params) }),
    // SLA countdowns go stale fast; a queue that lies about a deadline is
    // worse than one that costs an extra request.
    refetchInterval: 120_000,
  });
}

export function useTicketBoard(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['tickets', 'board', params],
    queryFn: () => apiGet<TicketBoard>('/tickets/board', { params: clean(params) }),
    refetchInterval: 120_000,
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['tickets', id],
    queryFn: () => apiGet<Ticket>(`/tickets/${id}`),
    enabled: Boolean(id),
    refetchInterval: 120_000,
  });
}

export function useSaveTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<Ticket>(id ? `/tickets/${id}` : '/tickets', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useAssignTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      apiPost<Ticket>(`/tickets/${id}/assign`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useReplyToTicket(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Ticket>(`/tickets/${id}/reply`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useSetTicketStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: string) => apiPost<Ticket>(`/tickets/${id}/status`, { status }),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useEscalateTicket(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Ticket>(`/tickets/${id}/escalate`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useLogTicketTime(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/tickets/${id}/time`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useConvertTicket(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/tickets/${id}/convert`, body),
    onSuccess: () => {
      invalidateService(queryClient);
      invalidateDelivery(queryClient);
    },
  });
}

export function useMergeTicket(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intoId: string) => apiPost(`/tickets/${id}/merge`, { intoId }),
    onSuccess: () => invalidateService(queryClient),
  });
}

// ── Contracts ────────────────────────────────────────────────────────────────

export function useContracts(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['contracts', params],
    queryFn: () => apiList<Contract[]>('/contracts', { params: clean(params) }),
  });
}

export function useExpiringContracts(withinDays = 90) {
  return useQuery({
    queryKey: ['contracts', 'expiring', withinDays],
    queryFn: () => apiGet<Contract[]>('/contracts/expiring', { params: { withinDays } }),
  });
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: ['contracts', id],
    queryFn: () => apiGet<Contract>(`/contracts/${id}`),
    enabled: Boolean(id),
  });
}

export function useContractProfitability(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['contracts', id, 'profitability'],
    queryFn: () => apiGet<ContractProfitability>(`/contracts/${id}/profitability`),
    enabled: Boolean(id) && enabled,
    retry: false,
  });
}

export function useSaveContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      apiPost<Contract>(id ? `/contracts/${id}` : '/contracts', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useSaveHourBank(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost(`/contracts/${contractId}/hour-banks`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useRollForwardHours(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bankId: string) =>
      apiPost(`/contracts/${contractId}/hour-banks/${bankId}/roll-forward`),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useRenewContract(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown> = {}) => apiPost<Contract>(`/contracts/${id}/renew`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useTerminateContract(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/contracts/${id}/terminate`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useSaveVisit(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/contracts/${contractId}/visits`, body),
    onSuccess: () => invalidateService(queryClient),
  });
}

// ── Service settings ─────────────────────────────────────────────────────────

export function useBusinessHours() {
  return useQuery({
    queryKey: ['service', 'business-hours'],
    queryFn: () => apiGet<BusinessHours[]>('/service/business-hours'),
  });
}

export function useSaveBusinessHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/service/business-hours', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useSlaPolicies() {
  return useQuery({
    queryKey: ['service', 'sla-policies'],
    queryFn: () => apiGet<SlaPolicy[]>('/service/sla-policies'),
  });
}

export function useSaveSlaPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/service/sla-policies', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useSlaCompliance(params: { from: string; to: string; companyId?: string; agentId?: string }) {
  return useQuery({
    queryKey: ['service', 'sla-compliance', params],
    queryFn: () => apiGet<SlaCompliance>('/service/sla-compliance', { params: clean(params) }),
  });
}

export function useCannedResponses() {
  return useQuery({
    queryKey: ['service', 'canned-responses'],
    queryFn: () => apiGet<CannedResponse[]>('/service/canned-responses'),
  });
}

export function useSaveCannedResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/service/canned-responses', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useDeleteCannedResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/service/canned-responses/${id}`),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useAssets(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['service', 'assets', params],
    queryFn: () => apiGet<Asset[]>('/service/assets', { params: clean(params) }),
  });
}

export function useSaveAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/service/assets', body),
    onSuccess: () => invalidateService(queryClient),
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/service/assets/${id}`),
    onSuccess: () => invalidateService(queryClient),
  });
}
